// Central procedure data. Category cards on the homepage + hub pages,
// plus a reusable data shape for individual procedure pages.

export const PROCEDURE_CATEGORIES = [
  {
    slug: "face-surgery-vietnam",
    key: "face",
    title: "Face",
    intro: "Facial rejuvenation and structural procedures.",
    items: [
      "Facelift",
      "Deep-plane facelift",
      "Face and neck lift",
      "Upper eyelid surgery",
      "Lower eyelid surgery",
      "Brow lift",
      "Facial fat grafting",
    ],
    image:
      "https://images.unsplash.com/photo-1764084455648-521862e715e5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    slug: "nose-surgery-vietnam",
    key: "nose",
    title: "Nose",
    intro: "Reshaping and structural correction of the nose.",
    items: [
      "Rhinoplasty",
      "Revision rhinoplasty",
      "Asian rhinoplasty",
      "Nasal reshaping and structural correction",
    ],
    image:
      "https://images.unsplash.com/photo-1775701662986-42a79196737c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    slug: "breast-surgery-vietnam",
    key: "breast",
    title: "Breast",
    intro: "Augmentation, lift, implant and revision procedures.",
    items: [
      "Breast augmentation",
      "Breast lift",
      "Implant replacement",
      "Implant removal",
      "Capsular contracture treatment",
      "Breast revision",
    ],
    image:
      "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    slug: "body-contouring-vietnam",
    key: "body",
    title: "Body",
    intro: "Contouring and body-shaping procedures.",
    items: [
      "Tummy tuck",
      "Liposuction",
      "Arm lift",
      "Thigh lift",
      "Mommy makeover",
      "Body lift and contouring",
    ],
    image:
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    slug: "revision-plastic-surgery-vietnam",
    key: "revision",
    title: "Revision Surgery",
    featured: true,
    intro: "Correction of previous surgery and complex cases.",
    items: [
      "Previous surgery correction",
      "Implant-related problems",
      "Scar revision",
      "Asymmetry",
      "Unsatisfactory results",
      "Complex corrective cases",
    ],
    image:
      "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

// Priority procedure routes (SEO). Grouped by category for hub pages.
export const PROCEDURE_ROUTES = {
  face: [
    { slug: "facelift-vietnam", title: "Facelift" },
    { slug: "deep-plane-facelift-vietnam", title: "Deep-Plane Facelift" },
    { slug: "face-neck-lift-vietnam", title: "Face & Neck Lift" },
    { slug: "eyelid-surgery-vietnam", title: "Eyelid Surgery" },
    { slug: "upper-blepharoplasty-vietnam", title: "Upper Blepharoplasty" },
    { slug: "lower-blepharoplasty-vietnam", title: "Lower Blepharoplasty" },
    { slug: "brow-lift-vietnam", title: "Brow Lift" },
  ],
  nose: [
    { slug: "rhinoplasty-vietnam", title: "Rhinoplasty" },
    { slug: "asian-rhinoplasty-vietnam", title: "Asian Rhinoplasty" },
    { slug: "revision-rhinoplasty-vietnam", title: "Revision Rhinoplasty" },
  ],
  breast: [
    { slug: "breast-augmentation-vietnam", title: "Breast Augmentation" },
    { slug: "breast-lift-vietnam", title: "Breast Lift" },
    { slug: "breast-implant-removal-vietnam", title: "Breast Implant Removal" },
    { slug: "breast-revision-vietnam", title: "Breast Revision" },
    { slug: "capsular-contracture-treatment-vietnam", title: "Capsular Contracture Treatment" },
  ],
  body: [
    { slug: "tummy-tuck-vietnam", title: "Tummy Tuck" },
    { slug: "liposuction-vietnam", title: "Liposuction" },
    { slug: "arm-lift-vietnam", title: "Arm Lift" },
    { slug: "thigh-lift-vietnam", title: "Thigh Lift" },
    { slug: "mommy-makeover-vietnam", title: "Mommy Makeover" },
  ],
};

// One fully-populated procedure used to demonstrate the reusable template.
export const SAMPLE_PROCEDURE = {
  slug: "rhinoplasty-vietnam",
  category: "nose",
  categorySlug: "nose-surgery-vietnam",
  categoryLabel: "Nose Surgery",
  h1: "Rhinoplasty in Vietnam",
  summary:
    "Rhinoplasty reshapes the nose for functional or aesthetic reasons. Whether a specific technique may be suitable can only be determined by a licensed surgeon after an in-person examination.",
  involves:
    "Rhinoplasty may involve reshaping bone and cartilage, refining the nasal tip, adjusting the bridge, or correcting structural issues affecting breathing. Techniques vary considerably between patients and surgeons.",
  suitable: [
    "Adults with fully developed nasal structures",
    "Non-smokers or those able to stop before and after surgery",
    "People in good general health",
    "Those with realistic, clearly discussed expectations",
  ],
  notSuitable: [
    "Patients with uncontrolled medical conditions until reviewed",
    "Those seeking guaranteed or idealised results",
    "Cases better addressed non-surgically after assessment",
  ],
  techniques: [
    "Open rhinoplasty",
    "Closed rhinoplasty",
    "Structural / preservation techniques",
    "Revision approaches for previous surgery",
  ],
  questions: [
    "Are you licensed to perform this procedure, and where can I verify this?",
    "In which licensed facility will surgery take place?",
    "How many similar cases have you managed?",
    "What are the realistic limitations in my case?",
    "What happens if a complication or revision is needed?",
  ],
  risks:
    "As with any surgery, risks may include bleeding, infection, scarring, asymmetry, breathing changes and the possibility of revision. Individual risk depends on health, anatomy, technique and aftercare.",
  outcome:
    "Results develop gradually as swelling settles, often over many months. Results vary between patients and are never guaranteed.",
  preparation: "Medical tests, medication review and lifestyle guidance may be advised before surgery.",
  anaesthesia: "General or local anaesthesia with sedation may be used, as advised by the medical team.",
  duration: "Approximately 1.5 to 3 hours, depending on complexity.",
  hospitalStay: "Often a day case or a short stay, depending on the surgeon's guidance.",
  recovery:
    "Initial swelling and bruising typically improve within two weeks; splints are usually removed within one to two weeks. Full refinement continues over several months.",
  stayInVietnam: "A stay of around 10–14 days is commonly discussed, but the exact period depends on the surgeon's travel-clearance advice.",
  flyingHome: "Flying should only take place once cleared by the treating surgeon.",
  costFactors: [
    "Surgeon and facility",
    "Anaesthesia",
    "Primary versus revision surgery",
    "Tests and follow-up care",
  ],
};
